import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { doc, onSnapshot } from 'firebase/firestore';
import { ArrowLeft, Loader2, Trash2 } from 'lucide-react';
import { db } from '@/lib/firebase';
import { useUnitContext } from '@/contexts/UnitContext';
import {
  archiveTreasurerMember,
  createTreasurerMember,
  updateTreasurerMember,
} from '@/lib/treasurerApi';
import { showError, showSuccess } from '@/lib/swal';

type Status = 'active' | 'honorary' | 'joining' | 'resigned' | 'deceased' | 'suspended';

interface FormState {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  membershipNumber: string;
  status: Status;
  joinedDate: string;
  leftDate: string;
  annualSubscriptionPounds: string;
  capitationExempt: boolean;
  paymentReference: string;
  notes: string;
}

const EMPTY: FormState = {
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  membershipNumber: '',
  status: 'active',
  joinedDate: '',
  leftDate: '',
  annualSubscriptionPounds: '',
  capitationExempt: false,
  paymentReference: '',
  notes: '',
};

function poundsToPence(pounds: string): number | null {
  const cleaned = pounds.trim().replace(/[£,\s]/g, '');
  if (cleaned === '' || !/^\d+(\.\d{0,2})?$/.test(cleaned)) return null;
  const [whole = '0', frac = ''] = cleaned.split('.');
  const padded = (frac + '00').slice(0, 2);
  return Number.parseInt(whole, 10) * 100 + Number.parseInt(padded, 10);
}

function penceToPounds(pence: number | undefined | null): string {
  if (typeof pence !== 'number' || !Number.isFinite(pence)) return '';
  return (pence / 100).toFixed(2);
}

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

interface LocationFromRoster {
  fromRoster?: {
    linkedUserId: string;
    firstName: string;
    lastName: string;
    email: string | null;
  };
}

export default function MemberFormPage() {
  const { memberId } = useParams<{ memberId?: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const linkedUserIdForCreate = useRef<string | null>(null);
  const rosterPrefillApplied = useRef(false);
  const [prefilledFromUnitRoster, setPrefilledFromUnitRoster] = useState(false);
  const { activeUnit, isTreasurer } = useUnitContext();
  const isEditing = Boolean(memberId);

  const [form, setForm] = useState<FormState>(EMPTY);
  const [loading, setLoading] = useState<boolean>(isEditing);
  const [submitting, setSubmitting] = useState(false);
  const [archiving, setArchiving] = useState(false);

  useEffect(() => {
    if (!activeUnit || memberId) return;
    if (rosterPrefillApplied.current) return;
    const st = (location.state as LocationFromRoster | null)?.fromRoster;
    if (!st?.linkedUserId) return;
    rosterPrefillApplied.current = true;
    linkedUserIdForCreate.current = st.linkedUserId;
    setPrefilledFromUnitRoster(true);
    setForm({
      ...EMPTY,
      firstName: st.firstName,
      lastName: st.lastName,
      email: st.email ?? '',
      joinedDate: todayIso(),
      annualSubscriptionPounds: '',
    });
  }, [activeUnit, memberId, location.state]);

  useEffect(() => {
    if (!activeUnit || !memberId) return;
    setLoading(true);
    const unsub = onSnapshot(
      doc(db, 'units', activeUnit.unitId, 'treasurerMembers', memberId),
      (snap) => {
        if (snap.exists()) {
          const d = (snap.data() ?? {}) as {
            firstName?: string;
            lastName?: string;
            email?: string;
            phone?: string;
            membershipNumber?: string;
            status?: Status;
            joinedDate?: string;
            leftDate?: string;
            annualSubscriptionPence?: number;
            capitationExempt?: boolean;
            paymentReference?: string;
            notes?: string;
          };
          setForm({
            firstName: d.firstName ?? '',
            lastName: d.lastName ?? '',
            email: d.email ?? '',
            phone: d.phone ?? '',
            membershipNumber: d.membershipNumber ?? '',
            status: (d.status as Status) ?? 'active',
            joinedDate: d.joinedDate ?? '',
            leftDate: d.leftDate ?? '',
            annualSubscriptionPounds: penceToPounds(d.annualSubscriptionPence),
            capitationExempt: d.capitationExempt === true,
            paymentReference: d.paymentReference ?? '',
            notes: d.notes ?? '',
          });
        }
        setLoading(false);
      },
      (err) => {
        console.error('Failed to load member', err);
        setLoading(false);
      },
    );
    return () => unsub();
  }, [activeUnit, memberId]);

  const validation = useMemo(() => {
    const errors: Partial<Record<keyof FormState, string>> = {};
    if (!form.firstName.trim()) errors.firstName = 'Required';
    if (!form.lastName.trim()) errors.lastName = 'Required';
    if (form.email.trim() && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email)) errors.email = 'Invalid email';
    if (!/^\d{4}-\d{2}-\d{2}$/.test(form.joinedDate)) errors.joinedDate = 'YYYY-MM-DD';
    if (form.leftDate && !/^\d{4}-\d{2}-\d{2}$/.test(form.leftDate)) errors.leftDate = 'YYYY-MM-DD';
    if (poundsToPence(form.annualSubscriptionPounds) === null) errors.annualSubscriptionPounds = 'Enter an amount';
    return errors;
  }, [form]);

  const isValid = Object.keys(validation).length === 0;

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!isValid || !activeUnit) return;
    if (!isTreasurer) {
      showError('Only the Treasurer can edit members');
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        unitId: activeUnit.unitId,
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        email: form.email.trim() || null,
        phone: form.phone.trim() || null,
        membershipNumber: form.membershipNumber.trim() || null,
        status: form.status,
        joinedDate: form.joinedDate,
        leftDate: form.leftDate || null,
        annualSubscriptionPence: poundsToPence(form.annualSubscriptionPounds) ?? 0,
        capitationExempt: form.capitationExempt,
        paymentReference: form.paymentReference.trim(),
        notes: form.notes.trim() || null,
      };

      if (isEditing && memberId) {
        const res = await updateTreasurerMember({ ...payload, memberId });
        showSuccess(
          res.hasReferenceCollision
            ? 'Saved — note that another member shares this payment reference'
            : 'Member updated',
        );
      } else {
        const res = await createTreasurerMember({
          ...payload,
          ...(linkedUserIdForCreate.current
            ? { linkedUserId: linkedUserIdForCreate.current }
            : {}),
        });
        showSuccess(
          res.hasReferenceCollision
            ? 'Member added — note that another member shares this payment reference'
            : 'Member added',
        );
        navigate(`/u/${activeUnit.unitId}/members/${res.memberId}`);
        return;
      }
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to save member');
    } finally {
      setSubmitting(false);
    }
  }

  async function handleArchive() {
    if (!activeUnit || !memberId) return;
    if (!window.confirm('Archive this member? Their historical records remain intact.')) return;
    setArchiving(true);
    try {
      await archiveTreasurerMember({
        unitId: activeUnit.unitId,
        memberId,
        reason: 'Archived from member detail screen',
      });
      showSuccess('Member archived');
      navigate(`/u/${activeUnit.unitId}/members`);
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to archive');
    } finally {
      setArchiving(false);
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-6 w-6 animate-spin text-emerald-600" />
      </div>
    );
  }

  return (
    <div className="max-w-3xl">
      <Link
        to={`/u/${activeUnit?.unitId}/members`}
        className="inline-flex items-center gap-1 text-sm text-slate-600 hover:text-slate-900 mb-3"
      >
        <ArrowLeft className="h-4 w-4" /> Back to members
      </Link>

      <h1 className="text-2xl font-bold text-slate-900 mb-1">
        {isEditing ? `${form.firstName} ${form.lastName}`.trim() || 'Edit member' : 'Add member'}
      </h1>
      <p className="text-slate-500 text-sm mb-6">
        {prefilledFromUnitRoster
          ? 'This record is linked to a lodge member account. Add subscription and payment details below.'
          : 'Members listed here are the people whose subscriptions and payments the Treasurer tracks.'}
      </p>

      <form onSubmit={handleSubmit} className="bg-white rounded-xl border border-slate-200 p-6 space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field
            label="First name"
            value={form.firstName}
            onChange={(v) => setForm((p) => ({ ...p, firstName: v }))}
            error={validation.firstName}
          />
          <Field
            label="Last name"
            value={form.lastName}
            onChange={(v) => setForm((p) => ({ ...p, lastName: v }))}
            error={validation.lastName}
          />
          <Field
            label="Email (optional)"
            type="email"
            value={form.email}
            onChange={(v) => setForm((p) => ({ ...p, email: v }))}
            error={validation.email}
          />
          <Field
            label="Phone (optional)"
            value={form.phone}
            onChange={(v) => setForm((p) => ({ ...p, phone: v }))}
          />
          <Field
            label="Grand Lodge membership number"
            value={form.membershipNumber}
            onChange={(v) => setForm((p) => ({ ...p, membershipNumber: v }))}
          />
          <SelectField
            label="Status"
            value={form.status}
            onChange={(v) => setForm((p) => ({ ...p, status: v as Status }))}
            options={[
              { value: 'active', label: 'Active' },
              { value: 'honorary', label: 'Honorary' },
              { value: 'joining', label: 'Joining' },
              { value: 'resigned', label: 'Resigned' },
              { value: 'deceased', label: 'Deceased' },
              { value: 'suspended', label: 'Suspended' },
            ]}
          />
          <Field
            label="Joined date"
            type="date"
            value={form.joinedDate}
            onChange={(v) => setForm((p) => ({ ...p, joinedDate: v }))}
            error={validation.joinedDate}
          />
          <Field
            label="Left date (if applicable)"
            type="date"
            value={form.leftDate}
            onChange={(v) => setForm((p) => ({ ...p, leftDate: v }))}
            error={validation.leftDate}
          />
          <Field
            label="Annual subscription (£)"
            value={form.annualSubscriptionPounds}
            onChange={(v) => setForm((p) => ({ ...p, annualSubscriptionPounds: v }))}
            placeholder="e.g. 120.00"
            error={validation.annualSubscriptionPounds}
          />
          <Field
            label="Payment reference"
            value={form.paymentReference}
            onChange={(v) => setForm((p) => ({ ...p, paymentReference: v }))}
            placeholder="e.g. SMITH001"
          />
        </div>

        <label className="flex items-center gap-2 text-sm text-slate-700">
          <input
            type="checkbox"
            checked={form.capitationExempt}
            onChange={(e) => setForm((p) => ({ ...p, capitationExempt: e.target.checked }))}
          />
          Exempt from Grand Lodge capitation (e.g. honorary, joining year)
        </label>

        <label className="block">
          <span className="text-xs font-medium text-slate-700">Notes (optional)</span>
          <textarea
            rows={4}
            value={form.notes}
            onChange={(e) => setForm((p) => ({ ...p, notes: e.target.value }))}
            className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:border-emerald-500"
          />
        </label>

        <div className="flex justify-between gap-3 pt-2 border-t border-slate-200">
          <div>
            {isEditing && isTreasurer && (
              <button
                type="button"
                disabled={archiving}
                onClick={handleArchive}
                className="inline-flex items-center gap-2 px-3 py-2 text-sm text-red-600 hover:text-red-700 disabled:opacity-50"
              >
                {archiving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                Archive member
              </button>
            )}
          </div>
          <div className="flex gap-3">
            <button
              type="button"
              onClick={() => navigate(`/u/${activeUnit?.unitId}/members`)}
              className="px-4 py-2 text-sm font-medium text-slate-600 hover:text-slate-900"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!isValid || submitting || !isTreasurer}
              className="inline-flex items-center gap-2 px-5 py-2 bg-emerald-600 text-white rounded-md font-medium disabled:opacity-50 hover:bg-emerald-700"
            >
              {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
              {isEditing ? 'Save changes' : 'Add member'}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}

function Field({
  label,
  value,
  onChange,
  type = 'text',
  placeholder,
  error,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  type?: string;
  placeholder?: string;
  error?: string;
}) {
  return (
    <label className="block">
      <span className="text-xs font-medium text-slate-700">{label}</span>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className={`mt-1 w-full rounded-md border px-3 py-2 text-sm focus:outline-none ${
          error ? 'border-red-400 focus:border-red-500' : 'border-slate-300 focus:border-emerald-500'
        }`}
      />
      {error && <span className="text-xs text-red-600 mt-1 block">{error}</span>}
    </label>
  );
}

function SelectField({
  label,
  value,
  onChange,
  options,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: Array<{ value: string; label: string }>;
}) {
  return (
    <label className="block">
      <span className="text-xs font-medium text-slate-700">{label}</span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm bg-white focus:outline-none focus:border-emerald-500"
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
    </label>
  );
}
